import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { doc, getDoc, updateDoc, arrayRemove } from "firebase/firestore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { green, red } from "@mui/material/colors";

import MembershipManager from "./MembershipManager";
import InvoicesManager from "./InvoicesManager";
import MemberCardManager from "./MemberCardManager";

import { db } from "../firebase";

export default function MemberDetail({
  member,
  onPlanAssign,
  onRetryCharge,
  onClose,
}) {
  // local copy of member doc, so we can refresh after card saves
  const [freshMember, setFreshMember] = useState(member);
  const [pennantDetails, setPennantDetails] = useState([]);

  // Reload the member document from Firestore
  const reloadMember = useCallback(async () => {
    if (!member?.id) return;
    const snap = await getDoc(doc(db, "members", member.id));
    if (snap.exists()) {
      setFreshMember({ id: snap.id, ...snap.data() });
    }
  }, [member]);

  // Whenever the prop changes, seed local state and reload
  useEffect(() => {
    setFreshMember(member);
    reloadMember();
  }, [member, reloadMember]);

  // Fetch pennant details for freshMember
  useEffect(() => {
    const fetchPennants = async () => {
      const entries = freshMember?.pennants;
      if (!entries?.length) {
        setPennantDetails([]);
        return;
      }
      try {
        const details = await Promise.all(
          entries.map(async ({ pennantId, section }) => {
            const snap = await getDoc(doc(db, "Pennants", pennantId));
            const data = snap.exists() ? snap.data() : {};
            return { id: pennantId, ...data, section };
          })
        );
        setPennantDetails(details.filter(Boolean));
      } catch (err) {
        console.error("Error fetching pennant details:", err);
      }
    };
    fetchPennants();
  }, [freshMember?.pennants]);

  // Remove member from a pennant
  const handleRemovePennant = async (pennantId) => {
    try {
      // 1) Remove from member doc
      await updateDoc(doc(db, "members", freshMember.id), {
        pennants: arrayRemove({ pennantId }),
      });
      // 2) Remove from pennant doc
      const pennantSnap = await getDoc(doc(db, "Pennants", pennantId));
      if (pennantSnap.exists()) {
        const data = pennantSnap.data();
        const updated = { ...data.members };
        Object.keys(updated).forEach((sec) => {
          updated[sec] = updated[sec].filter((mid) => mid !== freshMember.id);
        });
        await updateDoc(doc(db, "Pennants", pennantId), { members: updated });
      }
      // 3) Update local state
      setPennantDetails((prev) => prev.filter((p) => p.id !== pennantId));
    } catch (err) {
      console.error(`Error removing pennant ${pennantId}:`, err);
    }
  };

  if (!freshMember) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No member selected
        </Typography>
      </Box>
    );
  }

  const {
    firstName,
    lastName,
    email,
    phoneNumber,
    givenName,
    familyName,
    birthday,
    gender,
    profilePic,
    isActive,
    createdAt,
    updatedAt,
    savedPaymentMethods = [],
    subscriptions = [],
    note,
    squareCustomerId,
    address = {},
    squashPlayer,
    racquetballPlayer,
  } = freshMember;

  const { addressLine1, addressLine2, city, state, postalCode } = address;

  const formatPhoneNumber = (num) =>
    num?.startsWith("+61") ? `0${num.slice(3)}` : num;

  return (
    <Card sx={{ overflow: "hidden", position: "relative" }}>
      {/* Close */}
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        aria-label="close"
      >
        <CloseIcon />
      </IconButton>

      <CardContent>
        <Grid container spacing={3}>
          {/* Avatar & Status */}
          <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
            <Avatar
              alt={firstName || givenName}
              src={profilePic}
              sx={{
                width: 120,
                height: 120,
                mb: 2,
                border: "2px solid",
                borderColor: isActive ? "success.main" : "error.main",
              }}
            />
            <Typography variant="h5" gutterBottom>
              {firstName || givenName} {lastName || familyName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isActive ? "success.main" : "error.main",
                fontWeight: "bold",
              }}
            >
              {isActive ? "Active Member" : "Inactive Member"}
            </Typography>
            <Box sx={{ mt: 2, textAlign: "left" }}>
              <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                {squashPlayer ? (
                  <CheckCircleIcon sx={{ color: green[500], mr: 1 }} />
                ) : (
                  <CancelIcon sx={{ color: red[500], mr: 1 }} />
                )}
                <Typography>Squash Player</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {racquetballPlayer ? (
                  <CheckCircleIcon sx={{ color: green[500], mr: 1 }} />
                ) : (
                  <CancelIcon sx={{ color: red[500], mr: 1 }} />
                )}
                <Typography>Racquetball Player</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Member Details */}
          <Grid item xs={12} md={8}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Member Details
              </Typography>
              <Grid container spacing={2}>
                {/* Left */}
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            Email
                          </Typography>
                        }
                        secondary={<Typography>{email || "N/A"}</Typography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            Phone
                          </Typography>
                        }
                        secondary={
                          <Typography>{formatPhoneNumber(phoneNumber) || "N/A"}</Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            Gender
                          </Typography>
                        }
                        secondary={<Typography>{gender || "N/A"}</Typography>}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            Birthday
                          </Typography>
                        }
                        secondary={
                          <Typography>
                            {birthday
                              ? new Date(birthday).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>

                {/* Right */}
                <Grid item xs={12} sm={6}>
                  {(addressLine1 || city || state || postalCode) && (
                    <List dense>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            Address
                          </Typography>
                        }
                      />
                      {addressLine1 && (
                        <ListItem>
                          <ListItemText
                            secondary={<Typography>{addressLine1}</Typography>}
                          />
                        </ListItem>
                      )}
                      {addressLine2 && (
                        <ListItem>
                          <ListItemText
                            secondary={<Typography>{addressLine2}</Typography>}
                          />
                        </ListItem>
                      )}
                      {city && (
                        <ListItem>
                          <ListItemText
                            secondary={<Typography>{city}</Typography>}
                          />
                        </ListItem>
                      )}
                      {state && (
                        <ListItem>
                          <ListItemText
                            secondary={<Typography>{state}</Typography>}
                          />
                        </ListItem>
                      )}
                      {postalCode && (
                        <ListItem>
                          <ListItemText
                            secondary={<Typography>{postalCode}</Typography>}
                          />
                        </ListItem>
                      )}
                    </List>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Pennants */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Member's Pennants
            </Typography>
            {pennantDetails.length > 0 ? (
              <List>
                {pennantDetails.map((p, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={p.name || "Unknown Pennant"}
                      secondary={`Section: ${p.section || "N/A"} | Day: ${
                        p.pennantDay || "N/A"
                      } | Type: ${
                        p.participantType || "N/A"
                      } | Season: ${p.seasonName || "N/A"}`}
                    />
                    <Tooltip title="Remove member from this pennant">
                      <IconButton
                        edge="end"
                        aria-label="remove"
                        onClick={() => handleRemovePennant(p.id)}
                      >
                        <CancelIcon sx={{ color: red[500] }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Member is not part of any pennants.
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Membership */}
        <Typography variant="h6" gutterBottom>
          Membership
        </Typography>
        <MembershipManager
          squareCustomerId={squareCustomerId}
          subscriptions={subscriptions}
          onPlanAssign={onPlanAssign}
          onRetryCharge={onRetryCharge}
        />

        <Divider sx={{ my: 3 }} />

        {/* Invoices */}
        <Typography variant="h6" gutterBottom>
          Invoices
        </Typography>
        <InvoicesManager squareCustomerId={squareCustomerId} />

        <Divider sx={{ my: 3 }} />

        {/* Saved Payment Methods */}
        <Typography variant="h6" gutterBottom>
          Saved Payment Methods
        </Typography>
        {savedPaymentMethods.length > 0 ? (
          savedPaymentMethods.map((card, i) => (
            <Paper key={i} elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography>
                {card.cardBrand} ending in {card.last4}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Expires {card.expMonth}/{card.expYear}
              </Typography>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary">
            No saved payment methods
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* MemberCardManager with reload callback */}
        <Box sx={{ mt: 3 }}>
          <MemberCardManager
            member={freshMember}
            onCardSaved={reloadMember}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {note || "No notes available"}
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* History */}
        <Typography variant="h6" gutterBottom>
          History
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Created At"
              secondary={
                createdAt ? new Date(createdAt).toLocaleString() : "N/A"
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Updated At"
              secondary={
                updatedAt ? new Date(updatedAt).toLocaleString() : "N/A"
              }
            />
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
}

MemberDetail.propTypes = {
  member: PropTypes.object,
  onPlanAssign: PropTypes.func.isRequired,
  onRetryCharge: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
